import { useContext, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Grid,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  Tag,
  Image,
  Button,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useSession } from "next-auth/react";
import toast from "react-hot-toast";
import { AiFillLinkedin } from "react-icons/ai";
import { SettingsPopup } from "./settings-popup";
// import { DeleteTweet } from "../popups/delete-tweet";
import { TweetContext } from "context/tweetContext";
import { firebaseClient } from "firebaseClient";
import { border } from "theme/foundations/border";
import { radius } from "theme/foundations/radius";
import { text as textTheme } from "theme/foundations/text";
import { textStyle } from "theme/names";
import { getAccount } from "utils/sessionHelper";
import { StatsPopup } from "./stats-popup";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { checkTweetValid, scheduleTweet } from "controllers/schedule";
import { approve, reject } from "controllers/ghostController";

import { BiErrorAlt } from "react-icons/bi";
import { FiLink } from "react-icons/fi";
import { IoMdStats } from "react-icons/io";
import { FaRegComment } from "react-icons/fa";
import { BsCheckLg } from "react-icons/bs";
import { GrResume } from "react-icons/gr";
import { ImCross } from "react-icons/im";
import { BsChatSquareDots } from "react-icons/bs";
import { MdPendingActions, MdDownloadDone } from "react-icons/md";
import { RiDraftLine } from "react-icons/ri";
import { VscSettings } from "react-icons/vsc";

import { useWindowWidth } from "@react-hook/window-size";
import { EditIcon } from "./icons/edit-icon";
import { EditPageIcon } from "./icons/edit-page-icon";
import { EyeIcon } from "./icons/eye-icon";
import { OpenLinkedInIcon } from "./icons/open-linkedin-icon";

interface Props {
  type: "published" | "drafts" | "ghost" | "errors";
  tweet: any;
  time?: Date;
  bg?: any;
  onDelete?: (docId: string) => void;
  onAddToQueue?: (docId: string) => void;
  isGhostwriter?: boolean;
  isOrgDrafts?: boolean;
  disableIcon?: boolean;
  disableGhostInfo?: boolean;
}
const formatPostText = (text: string) => {
  if (!text) return text;

  const pollMatch = text.match(/#>poll:(.*?)#/);
  if (pollMatch) {
    try {
      text = text.replace(pollMatch[0], "[poll]");
    } catch (e) {
      console.error("Error parsing poll data:", e);
    }
  }

  const imageMatches = text.match(/\[img:(.*?)\]/g) || [];

  if (imageMatches.length > 0) {
    imageMatches.forEach(match => {
      switch (true) {
        case text.includes("vid-"):
          text = text.replace(match, "[video]");
          break;
        case text.includes("gif-"):
          text = text.replace(match, "[gif]");
          break;
        case text.includes("doc-"):
          text = text.replace(match, "[document]");
          break;
        default:
          text = text.replace(match, "[image]");
          break;
      }
    });
  }


  return text;
};


export function TweetBar({
  tweet,
  time,
  type,
  // bg = useColorModeValue("gray.50", "gray.800"),
  onDelete = () => {},
  onAddToQueue = () => {},
  isGhostwriter = false,
  isOrgDrafts = false,
  disableIcon = false,
  disableGhostInfo = false,
}: Props) {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState<boolean>(false);
  const [isLoadingVariation, setIsLoadingVariation] = useState<boolean>(false);
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = useDisclosure();
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenStats,
    onClose: onCloseStats,
    onOpen: onOpenStats,
  } = useDisclosure();
  const { data: session } = useSession() ?? {};
  const tweetContext: any = useContext(TweetContext);
  const iconColor = useColorModeValue("gray.600", "gray.400");
  const miscellaneousContext: any = useMiscellaneousContext();
  const screenWidth = useWindowWidth();

  const handleAddToQueue = async () => {
    let dataToSend = {
      idUser: getAccount(session)?.id,
      idAccount: getAccount(session)?.idAccount,
      twUserName: getAccount(session)?.twUserName,
      id: tweet.id,
      text: tweet.text,
      isScheduled: true,
      scheduledTime: tweetContext.nextSlot,
      isAutoPlug: false,
      isAutoRetweet: getAccount(session)?.isAutoRetweet ?? false,
      autoRetweetHours: getAccount(session)?.autoRetweetHours ?? 12,
      autoRetweetTimes: getAccount(session)?.autoRetweetTimes ?? 1,
      isPreventLinkExpand: getAccount(session)?.isPreventLinkExpand ?? 1,
      isPreventLinkExpandAutoPlug:
        getAccount(session)?.isPreventLinkExpand ?? 1,
      isRetweeted: false,
      isPlugged: false,
      autoRtWithOtherAccounts:
        getAccount(session)?.autoRtWithOtherAccounts ?? false,
    };

    if (
      !(await checkTweetValid([], session, tweetContext, dataToSend, undefined))
    ) {
      return;
    }
    await scheduleTweet(
      undefined,
      undefined,
      dataToSend,
      getAccount(session)?.timezone,
      tweetContext
    );
    // tweetContext.refreshNextSlot();
    tweetContext.refresher({});
  };

  // console.log(time);

  let bgColorDefault = useColorModeValue("white", "#1E1E1E");
  // let bgColorEvergreen = useColorModeValue("green.50", "green.900");
  let bgColorHovered = useColorModeValue(
    "border.lightMode.hover",
    "border.darkMode.hover"
  );
  let bgColorSlot = useColorModeValue(
    "background.lightMode.disabled",
    "background.darkMode.disabled"
  );

  let iconStatus = RiDraftLine;
  let iconStatusColor = "gray.400";
  if (tweet.statusGhost == "pending") {
    iconStatus = MdPendingActions;
    iconStatusColor = "gray.400";
  }
  if (tweet.statusGhost == "done" || tweet.statusGhost == "ready") {
    iconStatus = MdDownloadDone;
    iconStatusColor = "green.400";
  }
  if (tweet.statusGhost == "rejected") {
    iconStatus = BsChatSquareDots;
    iconStatusColor = "red.400";
  }

  const tweetLabels = useMemo(
    () => getAccount(session)?.tweetLabels,
    [session, miscellaneousContext.refreshLabels]
  );
  const labelsLinkedToTweet = tweetLabels?.filter((l) =>
    tweet?.labels?.includes(l.id)
  );

  console.log("type: " + type);
  const openPost = (tweet) => {
    if (type === "published" && tweet?.text) {
      tweetContext.setSelectedTweetPreview(tweet);
      tweetContext.onOpenTweetPreview();
      return;
    }
    onOpenSettings();
  };
  return (
    <Flex
      key={tweet.id ?? (time?.getTime && time.getTime())}
      borderRadius={radius.lg}
      mb={5}
      p={2}
      alignItems="center"
      bg={bgColorDefault}
      _hover={{
        boxShadow:
          "0px 0px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)",
      }}
      {...border.default.light}
      {...textTheme.body.medium.light.light}
      _dark={{
        ...border.default.dark,
        ...textTheme.body.medium.light.dark,
      }}
      cursor="pointer"
      onClick={async (e) => {
        openPost(tweet);
      }}
    >
      {/* published icon */}
      {type === "published" && (
        <Grid
          {...{
            display: ["none", "grid"], // hide on very small screens
            h: "32px",
            w: "32px",
            minW: "32px",
            bg: "success.lightMode.100",
            _dark: {
              bg: "success.darkMode.100",
            },
            borderRadius: radius.sm,
            fontSize: "14px",
            placeContent: "center",
          }}
        >
          <Icon
            {...{
              viewBox: "0 0 14 10",
            }}
          >
            <svg
              width="14"
              height="10"
              viewBox="0 0 14 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.9766 0.648438C13.332 0.976562 13.332 1.55078 12.9766 1.87891L5.97656 8.87891C5.64844 9.23438 5.07422 9.23438 4.74609 8.87891L1.24609 5.37891C0.890625 5.05078 0.890625 4.47656 1.24609 4.14844C1.57422 3.79297 2.14844 3.79297 2.47656 4.14844L5.34766 7.01953L11.7461 0.648438C12.0742 0.292969 12.6484 0.292969 12.9766 0.648438Z"
                fill="#31C52E"
              />
            </svg>
          </Icon>
        </Grid>
      )}
      {/* draft icon */}
      {["drafts", "errors"].includes(type) && (
        <Grid
          {...{
            display: ["none", "grid"], // hide on very small screens
            h: "32px",
            w: "32px",
            minW: "32px",
            bg: "background.lightMode.medium",
            _dark: {
              bg: "background.darkMode.medium",
            },
            borderRadius: radius.sm,
            fontSize: "14px",
            placeContent: "center",
          }}
        >
          <Icon
            {...{
              viewBox: "0 0 17 15",
            }}
          >
            <EditIcon />
          </Icon>
        </Grid>
      )}
      {/* <Flex alignItems="center" maxW={{ base: "95%", lg: "70%" }}> */}
      {type === "published" && time && (
        <Text
          textStyle={textStyle["body.medium.light"]}
          w="80px"
          minW="80px"
          ml={[2, 4]}
        >
          {format(time, "hh:mm aaa")}
        </Text>
      )}
      {["ghost"].includes(type) && (
        <Icon
          mr={0}
          m={2}
          color={iconStatusColor}
          fontSize="lg"
          as={iconStatus}
        />
      )}
      {!disableGhostInfo && tweet?.ghostwriterTwUserName && (
        <Flex ml={5} my={{ base: "3", lg: "0" }} alignItems="center">
          <Image
            mr={2}
            src={tweet.ghostwriterImage}
            rounded="full"
            w="6"
            h="6"
          />
          {screenWidth > 1100 && <Text>@{tweet.ghostwriterTwUserName}</Text>}
        </Flex>
      )}
      {(isGhostwriter || isOrgDrafts) && (
        <Flex ml={5} my={{ base: "3", lg: "0" }} alignItems="center">
          <Image
            mr={2}
            src={tweet.image ?? tweet.profilePic}
            rounded="full"
            w="6"
            h="6"
          />
          {screenWidth > 1100 && <Text>{tweet.name}</Text>}
        </Flex>
      )}
      {!["ghost"].includes(type) && (
        <Box pt={2} ml={["drafts", "errors"].includes(type) ? 5 : 0}>
          {/* <Icon as={AiFillLinkedin} fontSize="xl" color="#38A1D6" /> */}
        </Box>
      )}
      {tweet?.idCompany && (
        <Flex ml={2} p={1} h={8} w={8} minW={8} display={["none", "grid"]}>
          <Image rounded="full" src={tweet.image} />
        </Flex>
      )}
      {tweet && (
        <Text
          textStyle={textStyle["body.medium.light"]}
          ml={4}
          mr={12}
          flex={1}
          noOfLines={1}
          wordBreak={"break-all"}
          {...textTheme.body.medium.standard.light}
          _dark={{
            ...textTheme.body.medium.standard.dark,
          }}
        >
          {formatPostText(tweet.text)}
        </Text>
      )}
      {/* </Flex> */}
      {/* <Flex 
        alignItems="center"
        justifyContent="space-evenly"
        w={{ base: "100%", lg: "unset" }}
        mt={{ base: "1.5", lg: "0" }}
      >
      */}

      {["drafts", "errors"].includes(type) &&
        labelsLinkedToTweet?.length > 0 && (
          <Flex mr="2">
            {labelsLinkedToTweet.map((l) => (
              <Tag key={l.id} mr={2} variant="outline" colorScheme={l.color}>
                {l.title}
              </Tag>
            ))}
          </Flex>
        )}
      {type === "published" && tweet.status == "sent" && tweet.id_str && (
        <>
          <Tooltip hasArrow label="Open on linkedIn">
            <IconButton
              mr={2}
              aria-label="Open on LinkedIn"
              variant="action"
              size="table"
              as="a"
              target="_blank"
              href={"https://www.linkedin.com/feed/update/" + tweet.id_str}
              onClick={async (e) => {
                e.stopPropagation();
              }}
              icon={
                <OpenLinkedInIcon />
              }
            />
          </Tooltip>
          <Tooltip label="Stats">
            <IconButton
              aria-label="Stats"
              // colorScheme="blackAlpha"
              variant="action"
              size="table"
              mr={2}
              onClick={(e) => {
                e.stopPropagation();
                onOpenStats();
              }}
              icon={
                <Icon viewBox="0 0 14 14">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 0C8.3125 0 9 0.6875 9 1.5V12.5C9 13.3438 8.3125 14 7.5 14H6.5C5.65625 14 5 13.3438 5 12.5V1.5C5 0.6875 5.65625 0 6.5 0H7.5ZM7.5 1.5H6.5V12.5H7.5V1.5ZM2.5 6C3.3125 6 4 6.6875 4 7.5V12.5C4 13.3438 3.3125 14 2.5 14H1.5C0.65625 14 0 13.3438 0 12.5V7.5C0 6.6875 0.65625 6 1.5 6H2.5ZM2.5 7.5H1.5V12.5H2.5V7.5ZM10 3.5C10 2.6875 10.6562 2 11.5 2H12.5C13.3125 2 14 2.6875 14 3.5V12.5C14 13.3438 13.3125 14 12.5 14H11.5C10.6562 14 10 13.3438 10 12.5V3.5ZM11.5 12.5H12.5V3.5H11.5V12.5Z"
                      fill="currentColor"
                    />
                  </svg>
                </Icon>
              }
            />
          </Tooltip>
          <Tooltip label="Settings">
            <IconButton
              aria-label="Settings"
              variant="action"
              size="table"
              mr={2}
              icon={
                <Icon
                  // as={VscSettings} color={iconColor}
                  // /
                  viewBox="0 0 16 16"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 13C0 12.5938 0.3125 12.25 0.75 12.25H2.59375C2.90625 11.25 3.875 10.5 5 10.5C6.09375 10.5 7.0625 11.25 7.375 12.25H15.25C15.6562 12.25 16 12.5938 16 13C16 13.4375 15.6562 13.75 15.25 13.75H7.375C7.0625 14.7812 6.09375 15.5 5 15.5C3.875 15.5 2.90625 14.7812 2.59375 13.75H0.75C0.3125 13.75 0 13.4375 0 13ZM6 13C6 12.4688 5.53125 12 5 12C4.4375 12 4 12.4688 4 13C4 13.5625 4.4375 14 5 14C5.53125 14 6 13.5625 6 13ZM11 5.5C12.0938 5.5 13.0625 6.25 13.375 7.25H15.25C15.6562 7.25 16 7.59375 16 8C16 8.4375 15.6562 8.75 15.25 8.75H13.375C13.0625 9.78125 12.0938 10.5 11 10.5C9.875 10.5 8.90625 9.78125 8.59375 8.75H0.75C0.3125 8.75 0 8.4375 0 8C0 7.59375 0.3125 7.25 0.75 7.25H8.59375C8.90625 6.25 9.875 5.5 11 5.5ZM12 8C12 7.46875 11.5312 7 11 7C10.4375 7 10 7.46875 10 8C10 8.5625 10.4375 9 11 9C11.5312 9 12 8.5625 12 8ZM15.25 2.25C15.6562 2.25 16 2.59375 16 3C16 3.4375 15.6562 3.75 15.25 3.75H8.375C8.0625 4.78125 7.09375 5.5 6 5.5C4.875 5.5 3.90625 4.78125 3.59375 3.75H0.75C0.3125 3.75 0 3.4375 0 3C0 2.59375 0.3125 2.25 0.75 2.25H3.59375C3.90625 1.25 4.875 0.5 6 0.5C7.09375 0.5 8.0625 1.25 8.375 2.25H15.25ZM5 3C5 3.5625 5.4375 4 6 4C6.53125 4 7 3.5625 7 3C7 2.46875 6.53125 2 6 2C5.4375 2 5 2.46875 5 3Z"
                      fill="currentColor"
                    />
                  </svg>
                </Icon>
              }
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings();
              }}
            />
          </Tooltip>
        </>
      )}
      {["drafts", "ghost", "errors"].includes(type) && (
        <>
          {/* <IconButton
            aria-label="View post"
            colorScheme="blackAlpha"
            variant="ghost"
            size="sm"
            mr="2"
            icon={<Icon as={AiFillEye} color={iconColor} />}
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings();
            }}
          /> */}
          {tweet.isError && tweet.message && (
            <Tooltip hasArrow label={"An error occured: " + tweet.message}>
              <IconButton
                variant="action"
                aria-label="error"
                mr={2}
                size="sm"
                icon={<BiErrorAlt color="red" fontSize="18px" />}
                cursor="pointer"
                onClick={async (e) => {
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          )}
          <Tooltip hasArrow label="Preview">
            <IconButton
              aria-label="Preview"
              // color="twitter.600"
              variant="action"
              size="table"
              // size="sm"
              mr={2}
              onClick={(e) => {
                e.stopPropagation();
                onOpenSettings();
              }}
              icon={
                <Icon fontSize="22px">
                  <EyeIcon />
                </Icon>
              }
            />
          </Tooltip>
          <Tooltip hasArrow label="Edit">
            <IconButton
              aria-label="Edit"
              variant="action"
              size="table"
              // size="sm"
              // isLoading={isLoadingEdit}
              onClick={(e) => {
                e.stopPropagation();
                tweetContext.newTweet(tweet, tweet.id);
                tweetContext.setIsTweetTextChanged(true);
                tweetContext.open();
              }}
              icon={
                <Icon fontSize="22px">
                  <EditPageIcon />
                </Icon>
              }
            />
          </Tooltip>
          {/* <IconButton
              aria-label="re-write tweet"
              color="twitter.600"
              colorScheme="blackAlpha"
              variant="ghost"
              size="sm"
              mr="2"
              icon={
                <Icon
                  color={iconColor}
                  fontSize="lg"
                  as={HiOutlineLightningBolt}
                />
              }
              isLoading={isLoadingVariation}
              onClick={(e) => {
                e.stopPropagation();
                handleAiGeneration();
              }}
            /> */}
        </>
      )}

      {(["drafts", "errors"].includes(type) ||
        (tweet.userId === getAccount(session)?.id &&
          ["ghost"].includes(type))) && (
        <>
          <Tooltip hasArrow label="Delete">
            <IconButton
              ml={2}
              aria-label="Delete"
              variant="actionDestructive"
              size="table"
              // size="sm"
              isLoading={isDeleting}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  setIsDeleting(true);
                  const db = firebaseClient.firestore();
                  await db
                    .collection("users")
                    .doc(getAccount(session)?.id)
                    .collection("tweets")
                    .doc(tweet.id)
                    .delete();

                  setIsDeleting(false);
                  onDelete(tweet.id);
                } catch (e) {
                  setIsDeleting(false);
                  console.log("Error in deleting draft: ", e);
                  toast.error("Error in deleting draft: " + e.message);
                }
              }}
              icon={
                <Icon viewBox="0 0 14 16">
                  <svg
                    width="14"
                    height="16"
                    viewBox="0 0 14 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.25 2.5C13.6562 2.5 14 2.84375 14 3.25C14 3.6875 13.6562 4 13.25 4H12.875L12.125 14.1562C12.0312 15.2188 11.1875 16 10.125 16H3.84375C2.78125 16 1.9375 15.2188 1.84375 14.1562L1.09375 4H0.75C0.3125 4 0 3.6875 0 3.25C0 2.84375 0.3125 2.5 0.75 2.5H2.90625L4.0625 0.78125C4.375 0.3125 4.9375 0 5.53125 0H8.4375C9.03125 0 9.59375 0.3125 9.90625 0.78125L11.0625 2.5H13.25ZM5.53125 1.5C5.4375 1.5 5.34375 1.5625 5.3125 1.625L4.71875 2.5H9.25L8.65625 1.625C8.625 1.5625 8.53125 1.5 8.4375 1.5H5.53125ZM11.375 4H2.59375L3.34375 14.0625C3.375 14.3125 3.59375 14.5 3.84375 14.5H10.125C10.375 14.5 10.5938 14.3125 10.625 14.0625L11.375 4Z"
                      fill="currentColor"
                    />
                  </svg>
                </Icon>
              }
            />
          </Tooltip>
        </>
      )}
      {!isGhostwriter && ["drafts", "ghost", "errors"].includes(type) && (
        <Button
          variant="secondary"
          size="xs"
          ml={3}
          mr={1}
          fontSize="xs"
          borderRadius={"7px"}
          // colorScheme="blackAlpha"
          onClick={(e) => {
            e.stopPropagation();
            handleAddToQueue();
          }}
          // color={iconColor}
        >
          Add To Queue
        </Button>
      )}
      {!isGhostwriter && ["ghost"].includes(type) && (
        <Button
          variant={"outline"}
          size="xs"
          colorScheme="blackAlpha"
          ml={2}
          mr={1}
          isLoading={isLoadingApprove}
          onClick={async (e) => {
            e.stopPropagation();
            setIsLoadingApprove(true);
            await approve(tweet);
            setIsLoadingApprove(false);
          }}
          color={tweet.statusGhost == "ready" ? "green.400" : iconColor}
        >
          <Icon as={BsCheckLg} />
        </Button>
      )}
      {/* </Flex> */}
      <SettingsPopup
        isOpen={isOpenSettings}
        onClose={onCloseSettings}
        tweet={tweet}
        hideAdvancedButton={["drafts", "errors"].includes(type)}
        showAddToQueue={
          !isGhostwriter && ["drafts", "ghost", "errors"].includes(type)
        }
        isGhostwriter={isGhostwriter}
        handleAddToQueue={handleAddToQueue}
      />
      {/* <DeleteTweet
        tweetId={tweet.id_str}
        onClose={onCloseDelete}
        isOpen={isOpenDelete}
        tweet={tweet}
      /> */}
      <StatsPopup isOpen={isOpenStats} onClose={onCloseStats} tweet={tweet} />
    </Flex>
  );
}
